import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
  CreditCard,
  AlertCircle,
  Calendar,
  Users,
  Euro,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  Edit2,
  Save,
  X,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { format, startOfMonth, endOfMonth, subMonths, isAfter, isBefore } from 'date-fns';
import { tr } from 'date-fns/locale';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { convertCurrency, getExchangeRate, updateExchangeRate } from '../../utils/currency';

interface DashboardStats {
  monthlyIncome: number;
  monthlyExpenses: number;
  netProfit: number;
  totalStudents: number;
  pendingInvoices: number;
  overdueInvoices: number;
  completedPayments: number;
  pendingPayments: number;
  incomeChange: number;
  expenseChange: number;
}

interface RecentActivity {
  id: string;
  type: 'invoice' | 'payment' | 'expense' | 'document';
  title: string;
  amount?: number;
  currency?: string;
  date: string;
  status: string;
}

const AccountingDashboard: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    monthlyIncome: 0,
    monthlyExpenses: 0,
    netProfit: 0,
    totalStudents: 0,
    pendingInvoices: 0,
    overdueInvoices: 0,
    completedPayments: 0,
    pendingPayments: 0,
    incomeChange: 0,
    expenseChange: 0
  });
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [monthlyData, setMonthlyData] = useState<any[]>([]);
  const [upcomingPayments, setUpcomingPayments] = useState<any[]>([]);
  const [approvedProposals, setApprovedProposals] = useState<any[]>([]);
  const [overdueInstallments, setOverdueInstallments] = useState<any[]>([]);
  const [exchangeRate, setExchangeRate] = useState<number>(48.84);
  const [isEditingRate, setIsEditingRate] = useState(false);
  const [newRate, setNewRate] = useState<string>('48.84');
  const [isFetchingRate, setIsFetchingRate] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchCurrentExchangeRate();
  }, []);

  const fetchCurrentExchangeRate = async () => {
    const rate = await getExchangeRate('TRY', 'EUR');
    if (rate) {
      setExchangeRate(rate);
      setNewRate(rate.toFixed(2));
    }
  };

  const handleSaveExchangeRate = async () => {
    if (!user) return;
    const rateValue = parseFloat(newRate);
    if (isNaN(rateValue) || rateValue <= 0) {
      alert('Geçerli bir kur değeri girin');
      return;
    }

    const success = await updateExchangeRate('TRY', 'EUR', rateValue, user.id);
    if (success) {
      setExchangeRate(rateValue);
      setIsEditingRate(false);
      fetchDashboardData();
    } else {
      alert('Kur güncellenirken hata oluştu');
    }
  };

  const handleFetchFromTCMB = async (silent = false) => {
    setIsFetchingRate(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        if (!silent) alert('Oturum bulunamadı');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-exchange-rate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      console.log('Exchange rate API response:', result);

      if (response.ok && result.success) {
        setExchangeRate(result.rate);
        setNewRate(result.rate.toFixed(2));
        if (!silent) {
          alert(`Kur başarıyla güncellendi!\nYeni Kur: ₺${result.rate.toFixed(2)}\nKaynak: ${result.source}\nTarih: ${result.date || ''} ${result.time || ''}`);
          await fetchDashboardData();
        }
      } else {
        console.error('Exchange rate API error:', result);
        if (!silent) {
          const errorMsg = result.error || result.details || 'Bilinmeyen hata';
          alert(`Kur güncellenirken hata oluştu:\n${errorMsg}\n\nDetaylar için console loglarını kontrol edin.`);
        }
      }
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
      if (!silent) {
        alert(`Kur güncellenirken bir hata oluştu:\n${error instanceof Error ? error.message : 'Bilinmeyen hata'}\n\nDetaylar için console loglarını kontrol edin.`);
      }
    } finally {
      setIsFetchingRate(false);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchStats(),
        fetchMonthlyTrends(),
        fetchRecentActivities(),
        fetchUpcomingPayments(),
        fetchApprovedProposals(),
        fetchOverdueInstallments()
      ]);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const currentMonthStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');
    const currentMonthEnd = format(endOfMonth(new Date()), 'yyyy-MM-dd');
    const previousMonthStart = format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd');
    const previousMonthEnd = format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd');

    const [
      currentPayments,
      previousPayments,
      currentExpenses,
      previousExpenses,
      students,
      allInstallments
    ] = await Promise.all([
      supabase.from('installments').select('amount, payment_date').eq('status', 'paid').gte('payment_date', currentMonthStart).lte('payment_date', currentMonthEnd),
      supabase.from('installments').select('amount, payment_date').eq('status', 'paid').gte('payment_date', previousMonthStart).lte('payment_date', previousMonthEnd),
      supabase.from('expenses').select('amount, currency').eq('payment_status', 'paid').gte('expense_date', currentMonthStart).lte('expense_date', currentMonthEnd),
      supabase.from('expenses').select('amount, currency').eq('payment_status', 'paid').gte('expense_date', previousMonthStart).lte('expense_date', previousMonthEnd),
      supabase.from('students').select('id', { count: 'exact' }),
      supabase.from('installments').select('id, status, due_date')
    ]);

    const monthlyIncome = currentPayments.data?.reduce((sum, p) => sum + Number(p.amount), 0) || 0;
    const previousIncome = previousPayments.data?.reduce((sum, p) => sum + Number(p.amount), 0) || 0;

    let monthlyExpenses = 0;
    if (currentExpenses.data) {
      for (const exp of currentExpenses.data) {
        const amount = Number(exp.amount);
        const converted = await convertCurrency(amount, exp.currency || 'EUR', 'EUR');
        monthlyExpenses += converted;
      }
    }

    let previousExpenseTotal = 0;
    if (previousExpenses.data) {
      for (const exp of previousExpenses.data) {
        const amount = Number(exp.amount);
        const converted = await convertCurrency(amount, exp.currency || 'EUR', 'EUR');
        previousExpenseTotal += converted;
      }
    }

    const incomeChange = previousIncome > 0 ? ((monthlyIncome - previousIncome) / previousIncome) * 100 : 0;
    const expenseChange = previousExpenseTotal > 0 ? ((monthlyExpenses - previousExpenseTotal) / previousExpenseTotal) * 100 : 0;

    const today = new Date();
    const pendingInstallments = allInstallments.data?.filter(i => i.status === 'pending').length || 0;
    const overdueInstallments = allInstallments.data?.filter(i =>
      i.status === 'pending' && new Date(i.due_date) < today
    ).length || 0;

    const [completedPaymentsData, pendingPaymentsData] = await Promise.all([
      supabase.from('installments').select('id', { count: 'exact' }).eq('status', 'paid').gte('payment_date', currentMonthStart).lte('payment_date', currentMonthEnd),
      supabase.from('installments').select('id', { count: 'exact' }).eq('status', 'pending')
    ]);

    setStats({
      monthlyIncome,
      monthlyExpenses,
      netProfit: monthlyIncome - monthlyExpenses,
      totalStudents: students.count || 0,
      pendingInvoices: pendingInstallments,
      overdueInvoices: overdueInstallments,
      completedPayments: completedPaymentsData.count || 0,
      pendingPayments: pendingPaymentsData.count || 0,
      incomeChange,
      expenseChange
    });
  };

  const fetchMonthlyTrends = async () => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const date = subMonths(new Date(), i);
      const start = format(startOfMonth(date), 'yyyy-MM-dd');
      const end = format(endOfMonth(date), 'yyyy-MM-dd');

      const [payments, expenses] = await Promise.all([
        supabase.from('installments').select('amount').eq('status', 'paid').gte('payment_date', start).lte('payment_date', end),
        supabase.from('expenses').select('amount, currency').eq('payment_status', 'paid').gte('expense_date', start).lte('expense_date', end)
      ]);

      const income = payments.data?.reduce((sum, p) => sum + Number(p.amount), 0) || 0;

      let expense = 0;
      if (expenses.data) {
        for (const exp of expenses.data) {
          const amount = Number(exp.amount);
          const converted = await convertCurrency(amount, exp.currency || 'EUR', 'EUR');
          expense += converted;
        }
      }

      months.push({
        name: format(date, 'MMM', { locale: tr }),
        gelir: Math.round(income),
        gider: Math.round(expense),
        kar: Math.round(income - expense)
      });
    }
    setMonthlyData(months);
  };

  const fetchRecentActivities = async () => {
    const activities: RecentActivity[] = [];

    const [installments, expenses] = await Promise.all([
      supabase.from('installments').select(`
        id,
        amount,
        currency,
        status,
        payment_date,
        created_at,
        students (first_name, last_name),
        contracts (contract_number)
      `).order('created_at', { ascending: false }).limit(5),
      supabase.from('expenses').select(`
        id,
        title,
        amount,
        currency,
        payment_status,
        expense_date,
        created_at
      `).order('created_at', { ascending: false }).limit(5)
    ]);

    if (installments.data) {
      installments.data.forEach(inst => {
        const student = Array.isArray(inst.students) ? inst.students[0] : inst.students;
        activities.push({
          id: inst.id,
          type: 'payment',
          title: inst.status === 'paid'
            ? `${student ? `${student.first_name} ${student.last_name}` : 'Bilinmeyen Öğrenci'} - Ödeme Alındı`
            : `${student ? `${student.first_name} ${student.last_name}` : 'Bilinmeyen Öğrenci'} - Bekleyen Taksit`,
          amount: inst.amount,
          currency: inst.currency,
          date: inst.payment_date || inst.created_at,
          status: inst.status
        });
      });
    }

    if (expenses.data) {
      expenses.data.forEach(exp => {
        activities.push({
          id: exp.id,
          type: 'expense',
          title: `Gider: ${exp.title}`,
          amount: exp.amount,
          currency: exp.currency,
          date: exp.expense_date,
          status: exp.payment_status
        });
      });
    }

    activities.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    setRecentActivities(activities.slice(0, 10));
  };

  const fetchUpcomingPayments = async () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const nextMonth = format(endOfMonth(new Date()), 'yyyy-MM-dd');

    const { data } = await supabase
      .from('installments')
      .select(`
        id,
        amount,
        currency,
        due_date,
        students (first_name, last_name)
      `)
      .eq('status', 'pending')
      .gte('due_date', today)
      .lte('due_date', nextMonth)
      .order('due_date', { ascending: true })
      .limit(5);

    setUpcomingPayments(data || []);
  };

  const fetchApprovedProposals = async () => {
    const { data } = await supabase
      .from('leads')
      .select(`
        id,
        first_name,
        last_name,
        status,
        created_at,
        proposals (
          id,
          title,
          total_amount,
          currency
        )
      `)
      .eq('status', 'enrolled')
      .not('proposals', 'is', null)
      .order('created_at', { ascending: false })
      .limit(10);

    setApprovedProposals(data || []);
  };

  const fetchOverdueInstallments = async () => {
    const today = format(new Date(), 'yyyy-MM-dd');

    const { data } = await supabase
      .from('installments')
      .select(`
        id,
        installment_number,
        amount,
        currency,
        due_date,
        students (first_name, last_name),
        contracts (contract_number)
      `)
      .eq('status', 'pending')
      .lt('due_date', today)
      .order('due_date', { ascending: true })
      .limit(10);

    setOverdueInstallments(data || []);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-gray-50 via-gray-50 to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
        <div className="relative">
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-gray-200 dark:border-gray-700"></div>
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
        </div>
        <p className="mt-6 text-lg font-semibold text-gray-700 dark:text-gray-300">Dashboard yükleniyor...</p>
      </div>
    );
  }

  const renderExchangeRateCard = () => (
    <div className="bg-gradient-to-br from-amber-500 via-amber-600 to-orange-600 rounded-2xl p-6 text-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
      <div className="flex items-center justify-between mb-4">
        <div className="w-12 h-12 bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center ring-2 ring-white/20">
          <Euro className="w-6 h-6" />
        </div>
        {!isEditingRate && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleFetchFromTCMB(false)}
              disabled={isFetchingRate}
              className="p-2 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-all duration-200 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              title="TCMB'den güncel kuru çek"
            >
              <RefreshCw className={`w-4 h-4 ${isFetchingRate ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setIsEditingRate(true)}
              className="p-2 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-all duration-200 hover:scale-110 shadow-lg"
              title="Kuru manuel düzenle"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {isEditingRate ? (
        <div className="space-y-3">
          <p className="text-amber-50 text-sm font-semibold">EUR / TRY Kuru</p>
          <div className="space-y-3">
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-700 font-bold text-lg">₺</span>
              <input
                type="number"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                step="0.01"
                className="w-full pl-10 pr-4 py-3 text-lg font-bold rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-white/40 transition-all shadow-lg"
                placeholder="48.84"
                autoFocus
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveExchangeRate}
                className="flex-1 px-4 py-3 bg-green-500 rounded-xl hover:bg-green-600 transition-all duration-200 hover:scale-105 font-semibold text-sm flex items-center justify-center gap-2 shadow-xl"
              >
                <Save className="w-4 h-4" />
                Kaydet
              </button>
              <button
                onClick={() => {
                  setIsEditingRate(false);
                  setNewRate(exchangeRate.toFixed(2));
                }}
                className="flex-1 px-4 py-3 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-all duration-200 hover:scale-105 font-semibold text-sm flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                İptal
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <p className="text-amber-50 text-sm font-semibold mb-2">EUR / TRY</p>
          <p className="text-3xl font-black tracking-tight mb-2">₺{exchangeRate.toFixed(2)}</p>
          <p className="text-amber-50/90 text-sm font-medium">1 EUR = {exchangeRate.toFixed(2)} TRY</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-50 to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 p-6 space-y-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black bg-gradient-to-r from-gray-900 via-blue-800 to-teal-700 dark:from-white dark:via-blue-200 dark:to-teal-200 bg-clip-text text-transparent">
            Muhasebe Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2 text-lg font-medium">
            {format(new Date(), 'MMMM yyyy', { locale: tr })} dönemi özeti
          </p>
        </div>
        <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-2xl px-6 py-3 shadow-lg ring-1 ring-gray-200 dark:ring-gray-700">
          <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {format(new Date(), 'dd MMMM yyyy', { locale: tr })}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {renderExchangeRateCard()}

        <div className="bg-gradient-to-br from-emerald-500 via-green-600 to-teal-600 rounded-2xl p-6 text-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center ring-2 ring-white/20">
              <TrendingUp className="w-6 h-6" />
            </div>
            {stats.incomeChange !== 0 && (
              <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full ${stats.incomeChange > 0 ? 'bg-green-100/20' : 'bg-red-100/20'} backdrop-blur-sm`}>
                {stats.incomeChange > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                <span className="text-sm font-bold">{Math.abs(stats.incomeChange).toFixed(1)}%</span>
              </div>
            )}
          </div>
          <p className="text-emerald-50 text-sm font-semibold mb-2">Aylık Gelir</p>
          <p className="text-3xl font-black tracking-tight mb-2">€{Math.round(stats.monthlyIncome).toLocaleString()}</p>
          <p className="text-emerald-50/90 text-sm font-medium">{stats.completedPayments} ödeme tamamlandı</p>
        </div>

        <div className="bg-gradient-to-br from-rose-500 via-red-600 to-pink-600 rounded-2xl p-6 text-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center ring-2 ring-white/20">
              <TrendingDown className="w-6 h-6" />
            </div>
            {stats.expenseChange !== 0 && (
              <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full ${stats.expenseChange > 0 ? 'bg-red-100/20' : 'bg-green-100/20'} backdrop-blur-sm`}>
                {stats.expenseChange > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                <span className="text-sm font-bold">{Math.abs(stats.expenseChange).toFixed(1)}%</span>
              </div>
            )}
          </div>
          <p className="text-rose-50 text-sm font-semibold mb-2">Aylık Gider</p>
          <p className="text-3xl font-black tracking-tight mb-2">€{Math.round(stats.monthlyExpenses).toLocaleString()}</p>
          <p className="text-rose-50/90 text-sm font-medium">Bu ay toplam</p>
        </div>

        <div className={`bg-gradient-to-br ${stats.netProfit >= 0 ? 'from-blue-500 via-cyan-600 to-teal-600' : 'from-orange-500 via-amber-600 to-yellow-600'} rounded-2xl p-6 text-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1`}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center ring-2 ring-white/20">
              <DollarSign className="w-6 h-6" />
            </div>
          </div>
          <p className={`${stats.netProfit >= 0 ? 'text-blue-50' : 'text-orange-50'} text-sm font-semibold mb-2`}>Net Kar/Zarar</p>
          <p className="text-3xl font-black tracking-tight mb-2">{stats.netProfit >= 0 ? '€' : '-€'}{Math.abs(Math.round(stats.netProfit)).toLocaleString()}</p>
          <p className={`${stats.netProfit >= 0 ? 'text-blue-50/90' : 'text-orange-50/90'} text-sm font-medium`}>
            {stats.netProfit >= 0 ? 'Kar' : 'Zarar'} durumu
          </p>
        </div>

        <div className="bg-gradient-to-br from-sky-500 via-blue-600 to-cyan-600 rounded-2xl p-6 text-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center ring-2 ring-white/20">
              <FileText className="w-6 h-6" />
            </div>
            {stats.overdueInvoices > 0 && (
              <div className="bg-red-500 rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shadow-lg animate-pulse">
                {stats.overdueInvoices}
              </div>
            )}
          </div>
          <p className="text-sky-50 text-sm font-semibold mb-2">Bekleyen Taksitler</p>
          <p className="text-3xl font-black tracking-tight mb-2">{stats.pendingInvoices}</p>
          <p className="text-sky-50/90 text-sm font-medium">
            {stats.overdueInvoices > 0 ? `${stats.overdueInvoices} gecikmiş` : 'Gecikme yok'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white dark:bg-gray-900 rounded-2xl shadow-xl ring-1 ring-gray-200 dark:ring-gray-800 p-8 hover:shadow-2xl transition-all duration-300">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Aylık Gelir/Gider Trendi</h2>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" className="dark:stroke-gray-700" />
              <XAxis
                dataKey="name"
                stroke="#6B7280"
                style={{ fontSize: '13px', fontWeight: '600' }}
              />
              <YAxis
                stroke="#6B7280"
                style={{ fontSize: '13px', fontWeight: '600' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#F3F4F6',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
                  padding: '12px 16px'
                }}
                labelStyle={{ fontWeight: 'bold', marginBottom: '8px' }}
              />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="circle"
              />
              <Bar dataKey="gelir" fill="#10B981" name="Gelir (€)" radius={[10, 10, 0, 0]} />
              <Bar dataKey="gider" fill="#EF4444" name="Gider (€)" radius={[10, 10, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl ring-1 ring-gray-200 dark:ring-gray-800 p-8 hover:shadow-2xl transition-all duration-300">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Önemli Metrikler</h2>
          </div>
          <div className="space-y-5">
            <div className="group p-5 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-2xl border-2 border-emerald-200 dark:border-emerald-800 hover:border-emerald-400 dark:hover:border-emerald-600 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <CheckCircle className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">Tamamlanan Ödemeler</p>
                  <p className="text-3xl font-black text-gray-900 dark:text-white">{stats.completedPayments}</p>
                </div>
              </div>
            </div>

            <div className="group p-5 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-2xl border-2 border-orange-200 dark:border-orange-800 hover:border-orange-400 dark:hover:border-orange-600 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Clock className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">Bekleyen Ödemeler</p>
                  <p className="text-3xl font-black text-gray-900 dark:text-white">{stats.pendingPayments}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl ring-1 ring-gray-200 dark:ring-gray-800 p-8 hover:shadow-2xl transition-all duration-300">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Son Aktiviteler</h2>
          </div>
          <div className="space-y-3 max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 scrollbar-track-transparent">
            {recentActivities.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 font-medium">Henüz aktivite yok</p>
              </div>
            ) : (
              recentActivities.map((activity, index) => (
                <div
                  key={activity.id}
                  className="group flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-transparent dark:from-gray-800 dark:to-transparent rounded-xl hover:from-blue-50 hover:to-cyan-50 dark:hover:from-blue-900/10 dark:hover:to-cyan-900/10 transition-all duration-300 border border-transparent hover:border-blue-200 dark:hover:border-blue-800"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-300 ${activity.type === 'invoice' ? 'bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30' :
                      activity.type === 'payment' ? 'bg-gradient-to-br from-emerald-100 to-green-100 dark:from-emerald-900/30 dark:to-green-900/30' :
                        activity.type === 'expense' ? 'bg-gradient-to-br from-rose-100 to-red-100 dark:from-rose-900/30 dark:to-red-900/30' :
                          'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800'
                      }`}>
                      {activity.type === 'invoice' && <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
                      {activity.type === 'payment' && <Euro className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />}
                      {activity.type === 'expense' && <TrendingDown className="w-5 h-5 text-rose-600 dark:text-rose-400" />}
                      {activity.type === 'document' && <FileText className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{activity.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-0.5">
                        {format(new Date(activity.date), 'dd MMM yyyy HH:mm', { locale: tr })}
                      </p>
                    </div>
                  </div>
                  {activity.amount && (
                    <span className={`text-sm font-bold whitespace-nowrap ml-4 ${activity.type === 'payment' ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-900 dark:text-white'
                      }`}>
                      {activity.currency}{activity.amount.toLocaleString()}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl ring-1 ring-gray-200 dark:ring-gray-800 p-8 hover:shadow-2xl transition-all duration-300">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Yaklaşan Ödemeler</h2>
          </div>
          <div className="space-y-3 max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 scrollbar-track-transparent">
            {upcomingPayments.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 font-medium">Yaklaşan ödeme yok</p>
              </div>
            ) : (
              upcomingPayments.map((payment, index) => {
                const student = Array.isArray(payment.students) ? payment.students[0] : payment.students;
                return (
                  <div
                    key={payment.id}
                    className="group flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-transparent dark:from-gray-800 dark:to-transparent rounded-xl hover:from-orange-50 hover:to-amber-50 dark:hover:from-orange-900/10 dark:hover:to-amber-900/10 transition-all duration-300 border border-transparent hover:border-orange-200 dark:hover:border-orange-800"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 rounded-xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-300">
                        <Calendar className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {student ? `${student.first_name} ${student.last_name}` : 'Bilinmeyen'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-0.5">
                          Vade: {payment.due_date ? format(new Date(payment.due_date), 'dd MMM yyyy', { locale: tr }) : '-'}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap ml-4">
                      {payment.currency}{payment.amount.toLocaleString()}
                    </span>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl ring-1 ring-gray-200 dark:ring-gray-800 p-8 hover:shadow-2xl transition-all duration-300">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Onaylanan Teklifler</h2>
            </div>
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-3 py-1.5 rounded-full">
              Kayıt Olmuş Öğrenciler
            </span>
          </div>
          <div className="space-y-3 max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 scrollbar-track-transparent">
            {approvedProposals.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 font-medium">Onaylanan teklif yok</p>
              </div>
            ) : (
              approvedProposals.map((proposal, index) => (
                <div
                  key={proposal.id}
                  className="group flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-transparent dark:from-gray-800 dark:to-transparent rounded-xl hover:from-green-50 hover:to-emerald-50 dark:hover:from-green-900/10 dark:hover:to-emerald-900/10 transition-all duration-300 border border-transparent hover:border-green-200 dark:hover:border-green-800"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-300">
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {proposal.first_name} {proposal.last_name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-0.5 truncate">
                        {proposal.proposals && proposal.proposals.length > 0
                          ? `${proposal.proposals[0].university} - ${proposal.proposals[0].program}`
                          : 'Teklif bilgisi yok'}
                      </p>
                    </div>
                  </div>
                  {proposal.proposals && proposal.proposals.length > 0 && (
                    <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap ml-4">
                      {proposal.proposals[0].currency}{proposal.proposals[0].total_amount?.toLocaleString()}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/10 dark:to-rose-900/10 rounded-2xl shadow-xl ring-2 ring-red-200 dark:ring-red-800 p-8 hover:shadow-2xl transition-all duration-300">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl flex items-center justify-center animate-pulse">
                <AlertCircle className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-red-900 dark:text-red-100">Gecikmiş Taksitler</h2>
            </div>
            <span className="bg-red-500 text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-lg">
              {overdueInstallments.length} Uyarı
            </span>
          </div>
          <div className="space-y-3 max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-red-300 dark:scrollbar-thumb-red-700 scrollbar-track-transparent">
            {overdueInstallments.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-gray-600 dark:text-gray-300 font-medium">Gecikmiş taksit yok</p>
              </div>
            ) : (
              overdueInstallments.map((inst, index) => {
                const student = Array.isArray(inst.students) ? inst.students[0] : inst.students;
                const contract = Array.isArray(inst.contracts) ? inst.contracts[0] : inst.contracts;
                return (
                  <div
                    key={inst.id}
                    className="group flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/20 transition-all duration-300 border-2 border-red-300 dark:border-red-700 hover:border-red-500 dark:hover:border-red-500 shadow-sm hover:shadow-md"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-rose-100 dark:from-red-900/40 dark:to-rose-900/40 rounded-xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-300">
                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {student ? `${student.first_name} ${student.last_name}` : 'Bilinmeyen Öğrenci'}
                        </p>
                        <p className="text-xs text-red-600 dark:text-red-400 font-bold mt-0.5">
                          Vade: {inst.due_date ? format(new Date(inst.due_date), 'dd MMM yyyy', { locale: tr }) : '-'} - Taksit #{inst.installment_number}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-0.5 truncate">
                          {contract?.contract_number || 'Sözleşme No Yok'}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-red-600 dark:text-red-400 whitespace-nowrap ml-4">
                      {inst.currency}{inst.amount.toLocaleString()}
                    </span>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountingDashboard;
